export default class QuestionsScore {
    constructor() {
        this.question = null;
        this.answer = null;
    }

    setAnswer(question, answer) {
        this.question = question;
        this.answer = answer;
    }

    getScore() {
        const scoresMap = this.question.scores;

        if (!scoresMap) {
            return null;
        }

        /**
         * Calculate percentage scores
         */
        if (this.question.type === 'percentage') {
            return this._getPercentageScores();
        }

        /**
         * Calculate scores even for not selected answers
         */
        if (typeof scoresMap.GIVE_SCORES_FOR_NOT_SELECTED !== 'undefined') {
            return this._getEvenNotSelectedScores();
        }

        /**
         * Calculate all of the rest scores
         */
        return this._getRegularScores();
    }

    _getPercentageScores() {
        const scoresMap = this.question.scores;
        let score = {};

        this.answer.forEach(answer => {
            const scoresMapForCurrentAnswer = scoresMap[answer.index];
            if (!scoresMapForCurrentAnswer) {
                return;
            }

            /**
             * No logic, just add scores
             */
            if (!Array.isArray(scoresMapForCurrentAnswer)) {
                Object.keys(scoresMapForCurrentAnswer).forEach(comp => {
                    score = this._addScoreToTotal(score, comp, scoresMapForCurrentAnswer[comp]);
                });
                return true;
            }

            /**
             * With logic for the selected value
             */
            scoresMapForCurrentAnswer.some(condition => {
                if (answer.value > condition.value) {
                    return;
                }

                Object.keys(condition.scores).forEach(comp => {
                    score = this._addScoreToTotal(score, comp, condition.scores[comp]);
                });
                return true;
            });
        });

        return this._getAverageScores(score);
    }

    _getEvenNotSelectedScores() {
        let scoresMap = this.question.scores;
        delete scoresMap.GIVE_SCORES_FOR_NOT_SELECTED;
        let score = {};

        const answersIndexes = [];
        this.answer.forEach(answer => answersIndexes.push(answer.index));

        Object.keys(scoresMap).forEach(index => {
            index = parseInt(index);
            if (answersIndexes.indexOf(parseInt(index)) > -1) {
                Object.keys(scoresMap[index].SELECTED || []).forEach(comp => {
                    score = this._addScoreToTotal(score, comp, scoresMap[index].SELECTED[comp]);
                });
            } else {
                Object.keys(scoresMap[index].NOT_SELECTED || []).forEach(comp => {
                    score = this._addScoreToTotal(score, comp, scoresMap[index].SELECTED[comp]);
                });
            }
        });

        return this._getAverageScores(score);
    }

    _getRegularScores() {
        const scoresMap = this.question.scores;

        let score = {};

        this.answer.forEach(answer => {
            const scoresForCurrentAnswer = scoresMap[answer.index] || [];

            Object.keys(scoresForCurrentAnswer).forEach(comp => {
                score = this._addScoreToTotal(score, comp, scoresForCurrentAnswer[comp]);
            });
        });

        return this._getAverageScores(score);
    }

    _addScoreToTotal(total, competency, score) {
        total[competency] = total[competency] || {
            score: 0,
            answers: 0
        };

        total[competency].score += score;
        total[competency].answers += 1;

        return total;
    }

    _getAverageScores(total) {
        let score = {};

        Object.keys(total).forEach(comp => score[comp] = Math.round(total[comp].score / total[comp].answers * 10) / 10);

        return score;
    }

    getFeedback() {
        const feedbackMap = this.question.feedback;

        if (!feedbackMap) {
            return null;
        }

        let conditions = feedbackMap.conditions;
        let feedback = [];

        if (typeof conditions === 'undefined') {
            this.answer.every(answer => {
                const answerFeedback = feedbackMap[answer.index] || null;
                if (answerFeedback) {
                    if (Array.isArray(answerFeedback)) {
                        return answerFeedback.forEach(fb => feedback.push(fb));
                    } else {
                        return feedback.push(answerFeedback);
                    }
                }
                return true;
            });
        }
        /**
         * Conditions, complex mapping
         */
        else {
            const allIncluded = (stack, needle) => needle.every(v => stack.includes(v));
            const anyIncluded = (stack, needle) => needle.some(v => stack.includes(v));

            conditions = Array.isArray(conditions) ? [conditions] : conditions;

            Object.values(conditions).forEach((conditions, index) => {
                const match = conditions.some(condition => {
                    const match = condition.conditions.every(c => {
                        const answers = this.answer;
                        const answer = answers[index];
                        const length = answers.length;
                        const operator = c.operator;
                        const value = c.value;
                        const cond = c.condition;
                        const answersIndexes = [];
                        answers.forEach(answer => answersIndexes.push(answer.index));

                        if (cond === 'LENGTH') {
                            if (operator === '<') {
                                return length < value;
                            }
                            if (operator === '=' || operator === '==') {
                                return length === value;
                            }
                            if (operator === '>') {
                                return length > value;
                            }
                            if (operator === '!') {
                                return length !== value;
                            }
                        }
                        else if (cond === 'SELECTED') {
                            if (operator === '!') {
                                return !allIncluded(answersIndexes, value);
                            }
                            return allIncluded(answersIndexes, value);
                        }
                        else if (cond === 'SELECTED_ANY') {
                            if (operator === '!') {
                                return !anyIncluded(answersIndexes, value);
                            }
                            return anyIncluded(answersIndexes, value);
                        }
                        else if (cond === 'VALUE') {
                            if (operator === '<') {
                                return answer.value < value;
                            }
                            if (operator === '=' || operator === '==') {
                                return answer.value === value;
                            }
                            if (operator === '>') {
                                return answer.value > value;
                            }
                            if (operator === '!') {
                                return answer.value !== value;
                            }
                        } else {
                            console.error(`Unknown condition ${cond}`);
                            return false;
                        }
                    });

                    if (match) {
                        if (Array.isArray(condition.outcome)) {
                            condition.outcome.forEach(fb => feedback.push(fb));
                        } else {
                            feedback.push(condition.outcome);
                        }
                    }

                    return match;
                });

                if (!match && typeof feedbackMap.default !== 'undefined' && feedbackMap.default) {
                    if (Array.isArray(feedbackMap.default)) {
                        feedbackMap.default.forEach(fb => feedback.push(fb));
                    } else {
                        feedback.push(feedbackMap.default);
                    }
                }
            });
        }

        feedback = feedback.filter((value, index, self) => self.indexOf(value) === index);

        return feedback.length ? feedback : null;
    }
}
